import React, {
  useState, useCallback, useEffect, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'antd';
import PropTypes from 'prop-types';

import ProgressUpdateInput from './ProgressUpdateInput';
import { getProgressFromState } from './progressHelpers';
import { updateProgress } from '../../reports/state/reports.actions';

export default function ProgressMaterialUpdate({
  projectId,
  phaseId,
  costcodeId,
  units,
  estimatedQuantity,
  hasWrite,
  activeCategoryId,
}) {
  const dispatch = useDispatch();

  const progressState = useSelector((state) => state.reports.progress);
  const {
    projectCostcodeDetails: taskState,
    projectProgressFilters,
  } = useSelector((state) => state.projects);

  // Determine if this is a custom category (string ID) or default category (number ID)
  const isCustomCategory = typeof activeCategoryId === 'string';
  const defaultCategoryId = activeCategoryId && !isCustomCategory ? activeCategoryId : null;
  const customCategoryId = activeCategoryId && isCustomCategory ? activeCategoryId : null;

  const { quantityUsed } = useMemo(() => (
    getProgressFromState({
      progressState,
      taskState,
      projectId,
      phaseId,
      costcodeId,
      defaultCategoryId,
      customCategoryId,
    })
  ), [
    progressState,
    taskState,
    projectId,
    phaseId,
    costcodeId,
    defaultCategoryId,
    customCategoryId,
  ]);

  const [showPopover, setShowPopover] = useState(false);
  const [value, setValue] = useState(quantityUsed);

  const onConfirm = useCallback(async () => {
    if (await dispatch(updateProgress({
      projectId,
      phaseId,
      costcodeId,
      quantityUsed: value,
      defaultCategoryId,
      customCategoryId,
    }))) {
      setShowPopover(false);
    }
  }, [value, dispatch, projectId, phaseId, costcodeId, defaultCategoryId, customCategoryId]);

  const onCancel = useCallback(() => {
    setShowPopover(false);
    setValue(quantityUsed);
  }, [quantityUsed]);

  const onChange = useCallback((newValue) => {
    setShowPopover(true);
    setValue(newValue);
  }, []);

  useEffect(() => {
    setValue(quantityUsed);

    setTimeout(() => {
      setShowPopover(false);
    });
  }, [quantityUsed]);

  const isOver = estimatedQuantity && value && value > estimatedQuantity;
  const overStyle = isOver ? { color: 'red' } : {};
  return (
    <Row style={{ height: '100%' }} align="middle" gutter={10}>
      <Col style={{ height: 32 }}>
        {
          hasWrite
            ? (
              <ProgressUpdateInput
                min={projectProgressFilters?.dateRange ? Number.MIN_SAFE_INTEGER : 0}
                title="Update Material Usage"
                showPopover={showPopover}
                onConfirm={onConfirm}
                onCancel={onCancel}
                onChange={onChange}
                value={value}
                isOver={isOver}
                style={overStyle}
                disabled={!!projectProgressFilters?.dateRange}
                precision={2}
              />
            )
            : value
        }

      </Col>
      {
        estimatedQuantity && (
          <Col
            style={{
              maxWidth: 80,
              fontSize: 12,
              ...overStyle,
            }}
          >
            {`/ ${estimatedQuantity}`}
          </Col>
        )
      }
      {
        units && (
          <Col style={{ maxWidth: 80, fontSize: 12 }}>
            {units}
          </Col>
        )
      }
    </Row>
  );
}

ProgressMaterialUpdate.propTypes = {
  projectId: PropTypes.string,
  phaseId: PropTypes.string,
  costcodeId: PropTypes.string,
  units: PropTypes.string,
  estimatedQuantity: PropTypes.number,
  hasWrite: PropTypes.bool,
  activeCategoryId: PropTypes.string,
};

ProgressMaterialUpdate.defaultProps = {
  projectId: undefined,
  phaseId: undefined,
  costcodeId: undefined,
  units: undefined,
  estimatedQuantity: undefined,
  hasWrite: false,
  activeCategoryId: undefined,
};
